import {
	Card,
	Button,
	Table,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Input,
	Select,
	DatePicker,
	notification,
	Badge,
	Tooltip,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	PiGlobeDuotone,
	PiEyeDuotone,
	PiMagnifyingGlassDuotone,
	PiFunnelDuotone,
	PiUserDuotone,
	PiReceiptDuotone,
	PiCalendarDuotone,
	PiShieldDuotone,
	PiLockKeyDuotone,
	PiCertificateDuotone,
	PiHouseDuotone,
} from "react-icons/pi";
import { requestGetDomainList } from "../../services/user.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

function DomainListPage() {
	const { teamId } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [domains, setDomains] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		search: searchParams.get("search") || "",
		status: searchParams.get("status") || "",
		extension: searchParams.get("extension") || "",
		dateRange: null,
	});

	useEffect(() => {
		document.title = "Cloudiva App | Domain";
		fetchDomains();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.search) params.set("search", filters.search);
		if (filters.status) params.set("status", filters.status);
		if (filters.extension) params.set("extension", filters.extension);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchDomains = () => {
		setLoading(true);
		const filterParts = [];

		if (filters.status) filterParts.push(`status:${filters.status}`);
		if (filters.extension) filterParts.push(`extension:${filters.extension}`);
		if (filters.dateRange && filters.dateRange.length === 2) {
			filterParts.push(`startDate:${filters.dateRange[0].toISOString()}`);
			filterParts.push(`endDate:${filters.dateRange[1].toISOString()}`);
		}

		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.search || "",
			filter: filterParts.join(","),
			order: "expiredAt:asc",
		};

		requestGetDomainList(teamId, params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const domainsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setDomains(domainsData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Gagal",
					description: error.message || "Terjadi kesalahan saat mengambil data domain.",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleTableChange = (pagination) => {
		setPagination(pagination);
	};

	const handleSearch = (value) => {
		setFilters((prev) => ({ ...prev, search: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "ACTIVE":
				return "green";
			case "SUSPENDED":
				return "red";
			case "EXPIRED":
				return "default";
			case "CANCELLED":
				return "volcano";
			default:
				return "blue";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Tertunda";
			case "ACTIVE":
				return "Aktif";
			case "SUSPENDED":
				return "Ditangguhkan";
			case "EXPIRED":
				return "Kedaluwarsa";
			case "CANCELLED":
				return "Dibatalkan";
			default:
				return status;
		}
	};

	const columns = [
		{
			title: "Domain",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiGlobeDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{record.number}</div>
					</div>
				</div>
			),
		},
		{
			title: "Ekstensi",
			dataIndex: "extension",
			key: "extension",
			render: (extension) => (
				<div>
					<Tag color="blue" style={{ margin: 0 }}>
						{extension?.name || "N/A"}
					</Tag>
					<div style={{ fontSize: "12px", color: "#666", marginTop: 2 }}>
						{formatCurrency(extension?.price?.amount, "IDR")}
					</div>
				</div>
			),
		},
		{
			title: "Pengaturan",
			key: "settings",
			render: (_, record) => (
				<div style={{ fontSize: "12px" }}>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCertificateDuotone
							size={12}
							style={{ marginRight: 4, color: record.settings?.autoRenew ? "#52c41a" : "#d9d9d9" }}
						/>
						<span style={{ color: record.settings?.autoRenew ? "#52c41a" : "#999" }}>
							Perpanjang otomatis: {record.settings?.autoRenew ? "On" : "Off"}
						</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiShieldDuotone
							size={12}
							style={{
								marginRight: 4,
								color: record.settings?.privacyProtection ? "#52c41a" : "#d9d9d9",
							}}
						/>
						<span style={{ color: record.settings?.privacyProtection ? "#52c41a" : "#999" }}>
							Privasi: {record.settings?.privacyProtection ? "On" : "Off"}
						</span>
					</div>
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiLockKeyDuotone
							size={12}
							style={{ marginRight: 4, color: record.settings?.locked ? "#ff4d4f" : "#d9d9d9" }}
						/>
						<span style={{ color: record.settings?.locked ? "#ff4d4f" : "#999" }}>
							Terkunci: {record.settings?.locked ? "Yes" : "No"}
						</span>
					</div>
				</div>
			),
		},
		{
			title: "Tim",
			dataIndex: "team",
			key: "team",
			render: (team) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiUserDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
					<div>
						<div style={{ fontWeight: 500, fontSize: "13px" }}>{team?.name || "N/A"}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							{team?.owner?.name || "N/A"}&lt;{team?.owner?.email || "N/A"}&gt;
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status, record) => (
				<Space size={0}>
					<Tag color={getStatusColor(status)} style={{ borderRadius: "12px" }}>
						{getStatusText(status)}
					</Tag>
					{dayjs(record.expiredAt).isBefore(dayjs().add(60, "day")) && (
						// <span style={{ color: "#ff4d4f" }}> (Expiring)</span>
						<Tag color="#ff4d4f" style={{ borderRadius: "12px" }}>
							Habis dalam {dayjs(record.expiredAt).diff(dayjs(), "day")} hari
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Dates",
			key: "dates",
			render: (_, record) => (
				<div style={{ fontSize: "12px" }}>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#666" }} />
						<span>Terdaftar: {dayjs(record.registeredAt).format("MMM DD, YYYY")}</span>
					</div>
					<div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
						<PiCalendarDuotone size={12} style={{ marginRight: 4, color: "#ff7a45" }} />
						<span
							style={{
								color: dayjs(record.expiredAt).isBefore(dayjs().add(30, "day"))
									? "#ff4d4f"
									: "#666",
							}}>
							Kedaluwarsa: {dayjs(record.expiredAt).format("MMM DD, YYYY")}
						</span>
					</div>
					{record.nextBilledAt && (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiReceiptDuotone size={12} style={{ marginRight: 4, color: "#1890ff" }} />
							<span>Tagihan Berikutnya: {dayjs(record.nextBilledAt).format("MMM DD, YYYY")}</span>
						</div>
					)}
				</div>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			align: "right",
			render: (_, record) => (
				<Space size="small">
					<Tooltip title="View Details">
						<Button
							type="text"
							size="small"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/${teamId}/domains/${record._id}`)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	const statusOptions = [
		{ label: "Semua Status", value: "" },
		{ label: "Tertunda", value: "PENDING" },
		{ label: "Aktif", value: "ACTIVE" },
		{ label: "Ditangguhkan", value: "SUSPENDED" },
		{ label: "Kedaluwarsa", value: "EXPIRED" },
		{ label: "Dibatalkan", value: "CANCELLED" },
	];

	const extensionOptions = [
		{ label: "Semua Ekstensi", value: "" },
		{ label: ".com", value: "COM" },
		{ label: ".net", value: "NET" },
		{ label: ".org", value: "ORG" },
		{ label: ".id", value: "ID" },
		{ label: ".co.id", value: "CO.ID" },
	];

	return (
		<div className="page-content">
			<div className="page-content-wrapper">
				<Card
					title={
						<Space>
							Domain Anda
							<Badge count={pagination.total} showZero></Badge>
						</Space>
					}
					extra={
						<Space>
							<Search
								placeholder="Search domains..."
								allowClear
								enterButton={<PiMagnifyingGlassDuotone />}
								style={{ width: 250 }}
								onSearch={handleSearch}
							/>
						</Space>
					}>
					{/* Filters */}
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<Select
								placeholder="Filter by status"
								allowClear
								style={{ width: "100%" }}
								suffixIcon={<PiFunnelDuotone />}
								options={statusOptions}
								value={filters.status || undefined}
								onChange={(value) => handleFilterChange("status", value)}
							/>
						</Col>
						<Col span={6}>
							<Select
								placeholder="Filter by extension"
								allowClear
								style={{ width: "100%" }}
								suffixIcon={<PiFunnelDuotone />}
								options={extensionOptions}
								value={filters.extension || undefined}
								onChange={(value) => handleFilterChange("extension", value)}
							/>
						</Col>
						<Col span={6}>
							<RangePicker
								style={{ width: "100%" }}
								placeholder={["Start Date", "End Date"]}
								value={filters.dateRange}
								onChange={(dates) => handleFilterChange("dateRange", dates)}
							/>
						</Col>
					</Row>

					<Table
						columns={columns}
						dataSource={domains}
						rowKey="_id"
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} domains`,
						}}
						onChange={handleTableChange}
						scroll={{ x: 1200 }}
						size="small"
					/>
				</Card>
			</div>
		</div>
	);
}

export default DomainListPage;
